import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { QrCode, PlayCircle, Ticket, Check, Package } from 'lucide-react';
export function Slide04Mechanics() {
  const steps = [{
    num: '01',
    icon: <QrCode className="w-8 h-8 text-white" data-id="element-156" />,
    title: 'Scan at Pump',
    desc: 'High-visibility QR codes placed directly on the gas pumps catch their attention.',
    color: 'from-emerald-500 to-green-400'
  }, {
    num: '02',
    icon: <PlayCircle className="w-8 h-8 text-white" data-id="element-157" />,
    title: 'Play the Game',
    desc: 'A fast, branded mini-game keeps them engaged while the tank fills.',
    color: 'from-accent to-green-400'
  }, {
    num: '03',
    icon: <Ticket className="w-8 h-8 text-white" data-id="element-158" />,
    title: 'Claim Inside',
    desc: 'Prizes must be redeemed inside the travel center or casino, driving foot traffic.',
    color: 'from-accent to-[#3b9eff]'
  }];
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 30
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 20
      }
    }
  };
  return <div className="w-full min-h-full flex flex-col justify-center p-[clamp(2rem,6vw,6rem)]" data-id="element-159">
      <div className="max-w-7xl mx-auto w-full" data-id="element-160">
        <div className="text-center mb-[clamp(2rem,4vw,3.5rem)]" data-id="element-161">
          <motion.h2 initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-[clamp(2.5rem,4vw,4rem)] font-bold leading-tight mb-4" data-id="element-162">
            How It Works
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-xl text-muted max-w-2xl mx-auto" data-id="element-163">
            A frictionless loop designed to convert idle time into active
            visits.
          </motion.p>
        </div>

        <motion.div variants={container} initial="hidden" animate="show" className="flex md:grid md:grid-cols-3 gap-6 md:gap-8 relative overflow-x-auto snap-x snap-mandatory hide-scrollbar pb-2 md:pb-0" data-id="element-164">
          {/* Connecting Line (Desktop) */}
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-surface-hover z-0" data-id="element-165">
            <motion.div initial={{
            width: '0%'
          }} animate={{
            width: '100%'
          }} transition={{
            duration: 1.5,
            delay: 0.5,
            ease: 'easeInOut'
          }} className="h-full bg-gradient-to-r from-accent-secondary via-accent to-accent" data-id="element-166" />
          </div>

          {steps.map((step, i) => <motion.div key={i} variants={item} className="relative z-10 flex flex-col items-center text-center min-w-[100%] md:min-w-0 snap-center" data-id="element-167">
              <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br ${step.color} p-0.5 mb-4 md:mb-5 shadow-2xl`} data-id="element-168">
                <div className="w-full h-full bg-surface rounded-[14px] flex items-center justify-center relative overflow-hidden" data-id="element-169">
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-20`} data-id="element-170" />
                  {step.icon}
                </div>
              </div>

              <div className="text-xs md:text-sm font-black text-accent tracking-widest mb-1 md:mb-2" data-id="element-171">
                STEP {step.num}
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-4" data-id="element-172">
                {step.title}
              </h3>
              <p className="text-sm md:text-base text-muted leading-relaxed max-w-xs" data-id="element-173">
                {step.desc}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Fully managed band */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9,
        duration: 0.5
      }} className="mt-[clamp(1.5rem,5vw,4rem)] rounded-2xl bg-surface border border-surface-hover p-[clamp(1.25rem,3vw,2.5rem)] flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8 shrink-0" data-id="element-174">
          <div className="flex-1" data-id="element-175">
            <div className="text-xs md:text-sm font-black text-accent tracking-widest uppercase mb-2 md:mb-3" data-id="element-176">
              Effortless for your team
            </div>
            <p className="text-base md:text-lg text-foreground leading-relaxed max-w-2xl" data-id="element-177">
              Chalkline runs the entire program end-to-end — game design, setup,
              compliance, player support, and reporting. All you do is hand out
              the prizes.
            </p>
            <div className="mt-4 md:mt-5 flex flex-wrap gap-2" data-id="element-178">
              {['Game design', 'Setup & QR', 'Compliance', 'Support', 'Reporting'].map(chip => <span key={chip} className="inline-flex items-center gap-1.5 px-2.5 py-1 md:px-3 md:py-1.5 rounded-full bg-background border border-surface-hover text-xs md:text-sm text-muted" data-id="element-179">
                  <Check size={14} className="text-accent" data-id="element-180" />
                  {chip}
                </span>)}
            </div>
          </div>

          <div className="lg:w-px lg:h-24 h-px w-full bg-surface-hover" data-id="element-181" />

          <div className="lg:w-56 shrink-0 text-center lg:text-left" data-id="element-182">
            <div className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-accent-secondary/10 border border-accent-secondary/30 mb-2 md:mb-3" data-id="element-183">
              <Package size={20} className="text-accent-secondary" data-id="element-184" />
            </div>
            <div className="text-xs md:text-sm font-bold text-muted tracking-widest uppercase mb-1" data-id="element-185">
              Your only lift
            </div>
            <div className="text-lg md:text-xl font-bold text-foreground" data-id="element-186">
              Prize fulfillment
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}